import { ComponentAnswer } from '@type';

import { CareTaskStore } from './care-task-store.types';

export const getPotentialActiveCareTaskForAnswering = (
  state: CareTaskStore
) => {
  const activeCareTask = state.careTasks.find(
    item =>
      item.siteUuid === state.taskAnswering.siteUuid &&
      item.template.id === state.taskAnswering.taskId
  );

  return activeCareTask;
};

export const getActiveCareTaskForAnswering = (state: CareTaskStore) => {
  const activeCareTask = getPotentialActiveCareTaskForAnswering(state);

  if (!activeCareTask)
    throw new Error('Care task template for answering does not exist!');

  return activeCareTask;
};

export const getActiveComponent = (state: CareTaskStore) => {
  const activeCareTask = getActiveCareTaskForAnswering(state);
  const components = state.taskAnswering.isAnsweringMain
    ? activeCareTask?.template.components
    : activeCareTask?.template.additionalComponents;

  const currentComponent =
    components && components[state.taskAnswering.navigator.current];

  if (!currentComponent)
    throw new Error(
      'Current Care task component for answering does not exist!'
    );

  return currentComponent;
};

export const getActiveComponentAnswer = <AnswerType>(
  state: CareTaskStore
): ComponentAnswer<AnswerType> | null => {
  const activeCareTask = getActiveCareTaskForAnswering(state);
  const activeComponent = getActiveComponent(state);
  if (!activeCareTask.answer) return null;

  const currentAnswerObject = activeCareTask.answer.find(
    item =>
      item.careplanCareplanComponentMapperId ===
      activeComponent.careplanCareplanComponentMapper.id
  );
  return currentAnswerObject
    ? (currentAnswerObject as ComponentAnswer<AnswerType>)
    : null;
};

export const getIsLastComponent = (state: CareTaskStore) => {
  const activeCareTask = getActiveCareTaskForAnswering(state);
  const activeComponentAnswer = getActiveComponentAnswer(state);
  const componentsLength = activeCareTask.template.components?.length;
  if (state.taskAnswering.isAnsweringMain)
    return (
      componentsLength &&
      ((activeComponentAnswer?.jumpTo &&
        activeComponentAnswer.jumpTo >= componentsLength) ||
        state.taskAnswering.navigator.current === componentsLength - 1)
    );

  return (
    activeCareTask.template.additionalComponents &&
    state.taskAnswering.navigator.current ===
      activeCareTask.template.additionalComponents.length - 1
  );
};

export const getAnsweringNavigator = (state: CareTaskStore) => {
  return state.taskAnswering.navigator;
};

export const getTotalScore = (state: CareTaskStore) => {
  const activeCareTask = getActiveCareTaskForAnswering(state);
  return activeCareTask.answer?.reduce((sum, item) => {
    return sum + (item.score || 0);
  }, 0);
};

export const getAdditionalComponents = (state: CareTaskStore) => {
  const isLastComponent = getIsLastComponent(state);
  if (!isLastComponent) return undefined;
  const activeCareTask = getActiveCareTaskForAnswering(state);
  const totalScore = getTotalScore(state);

  if (totalScore === undefined) return undefined;

  return activeCareTask.template.scoringScheme?.find(
    scoreScheme =>
      scoreScheme.max >= scoreScheme.min &&
      scoreScheme.min > -1 &&
      totalScore >= scoreScheme.min &&
      totalScore <= scoreScheme.max
  )?.components;
};

export const getHasAdditionalComponents = (state: CareTaskStore) => {
  const additionalComponents = getAdditionalComponents(state);
  return additionalComponents !== undefined;
};

export const getIsAnsweringMain = (state: CareTaskStore) => {
  return state.taskAnswering.isAnsweringMain;
};
